import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import DonutChart from 'Components/ComponentLibrary/Components/Vizualizations/DonutChart';
import CollapsibleCard from 'Components/ComponentLibrary/Components/Uncategorized/CollapsibleCard/CollapsibleCard';
import Typography from 'Components/UI/Typography';
import fr from 'facet-rest';
import MaxLossesAndReturns
  from 'Views/Page/Content/Templates/AccountProposalReviewTemplate/Content/MaxLossesAndReturnsSection';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import InvestmentReturnsSection
  from 'Views/Page/Content/Templates/AccountProposalReviewTemplate/Content/InvestmentReturnsSection';
import Button1 from 'Components/ComponentLibrary/Components/Buttons/Button1';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import CollapsibleDisclaimer from 'Components/ComponentLibrary/Components/Uncategorized/CollapsibleDisclaimer';
import { pickWithPriority } from 'Helpers/Affordances';
import {
  FDAlert,
  FDBox,
  FDClickableText,
  FDDialog,
  FDDialogActions,
  FDDialogContent,
  FDDialogTitle,
  FDPrimaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import { numberToWord } from 'Helpers/Formats';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';
import HoldingsSection from './HoldingsSection';
import { CardContainer } from './cardContainer';

const buildExclusionCardDescription = (excludedTickers = [], persona = undefined, onClick = () => {}) => {
  const hasExcludedTickers = excludedTickers.length > 0;
  const hasPersona = !!persona && persona !== 'None';
  const maxTickersExpanded = 5;
  const tickerListStr = excludedTickers.slice(0, Math.min(excludedTickers.length, maxTickersExpanded)).join(', ');
  const numTickersOverMax = excludedTickers.length - maxTickersExpanded;
  if (hasExcludedTickers && hasPersona) {
    if (excludedTickers.length > maxTickersExpanded) {
      const otherTickersStr = `${numberToWord(numTickersOverMax)} other ticker${numTickersOverMax > 1 ? 's' : ''}`;
      return <>Based on your preferences, {tickerListStr}, and <FDClickableText appearance={'standalone'} label={otherTickersStr} onClick={onClick} /> will be excluded from your directly indexed holdings. The "{persona}" option has also been applied to this account.</>;
    }
    return <>Based on your preferences, {tickerListStr} will be excluded from your directly indexed holdings. The <FDClickableText appearance={'standalone'} label={`"${persona}"`} onClick={onClick} /> option has also been applied to this account.</>;
  }
  if (hasExcludedTickers) {
    if (excludedTickers.length > 5) {
      const otherTickersStr = `${numberToWord(numTickersOverMax)} other ticker${numTickersOverMax > 1 ? 's' : ''}`;
      return <>Based on your preferences, {tickerListStr}, and <FDClickableText appearance={'standalone'} label={otherTickersStr} onClick={onClick} /> will be excluded from your directly indexed holdings.</>;
    }
    return `Based on your preferences, ${tickerListStr} will be excluded from your directly indexed holdings.`;
  }

  // only hasPersona
  return <>Based on your preferences, the <FDClickableText appearance={'standalone'} label={`"${persona}"`} onClick={onClick} /> option has been applied to this account.</>;
};

const useStyles = buildUseStyles('AccountProposalReviewTemplate', (theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  rootFullWidthLayout: {
    backgroundColor: theme.palette.facet.backgroundGray,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  containerFullWidthLayout: {
    backgroundColor: theme.palette.common.white,
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: theme.palette.facet.primaryGray,
    marginBottom: theme.spacing(2),
  },
  infoIcon: {
    fontSize: theme.typography.fontSize1Rem,
    marginLeft: theme.spacing(0.5),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  donutWrapper: {
    marginBottom: theme.spacing(2),
  },
  allocationInfo: {
    marginBottom: theme.spacing(2),
  },
  linkButton: {
    marginBottom: theme.spacing(2),
    fontSize: theme.typography.fontSize1Rem,
    fontWeight: theme.typography.fontWeightBold,
  },
  noHoverStyle: {
    '&:hover,&:focus': {
      textDecoration: 'underline',
    },
  },
})));

const AccountProposalReviewTemplate = ({
  className,
  fullWidthLayout,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const [directIndexExclusionDetailsOpen, setDirectIndexExclusionDetailsOpen] = useState(false);
  const { loadPageContentLink } = usePageContentContext();
  const {
    resource,
  } = useMetaLayoutContext();

  const HOLDINGS_DATA = [
    {
      value: parseInt(resource.attr('equityHoldings'), 10),
      name: `${parseInt(resource.attr('equityHoldings'), 10)}% Stocks`,
    },
    ...(resource.attr('alternativeIncomeHoldings') !== '0' ? [{
      value: parseInt(resource.attr('alternativeIncomeHoldings'), 10),
      name: `${parseInt(resource.attr('alternativeIncomeHoldings'), 10)}% Alts`,
      color: theme.palette.facet.purple,
    }] : []),
    {
      value: parseInt(resource.attr('fixedHoldings'), 10),
      name: `${parseInt(resource.attr('fixedHoldings'), 10)}% Bonds`,
      color: theme.palette.success.main,
    },
    ...(resource.attr('cashHoldings') !== '0' ? [{
      value: parseInt(resource.attr('cashHoldings'), 10),
      name: `${parseInt(resource.attr('cashHoldings'), 10)}% Cash`,
      color: theme.palette.facet.primaryGray2,
    }] : []),
  ];

  const cashOnlyAccount = resource.attr('cashHoldings') === '100';
  const showExclusionsInfo = resource.attr('directIndexingExcludedTickers') || resource.attr('directIndexingPersona');

  return (
    <Box
      className={classNames(classes.root, className, {
        [classes.rootFullWidthLayout]: fullWidthLayout,
      })}
    >
      <Box
        className={classNames(classes.container, {
          [classes.containerFullWidthLayout]: fullWidthLayout,
        })}
      >
        <Title className={classes.title} text={resource.attr('title')} test={'wizard-step-title'} />
        <Box className={classes.subtitle}>
          <Subtitle text={resource.attr('subtitle')} test={'wizard-step-subtitle'} />
        </Box>
        <Box className={classes.donutWrapper}>
          <DonutChart
            legendTitle={'Investment Mix'}
            data={HOLDINGS_DATA}
          />
        </Box>
        {!cashOnlyAccount && (
          <Typography className={classes.expenseRatio} variant={'h6'} color={'primary'}>
            {`Expense Ratio ${resource.attr('expenseRatio')}`}
          </Typography>
        )}
        <FDBox paddingX={2}>
          <FDStack direction={'column'} spacing={2} maxWidth={500} width={'100%'}>
            {/* Direct Indexing / Alternative Income */}
            {resource.attr('additionalInfoTitle') ? (
              <Box className={classes.card}>
                <FDAlert
                  status={'success'}
                  title={resource.attr('additionalInfoTitle')}
                  description={resource.attr('additionalInfoSubtitle')}
                />
              </Box>
            ) : null}
            {/* Direct Indexing Exclusions */}
            {showExclusionsInfo ? (
              <Box className={classes.card}>
                <FDAlert
                  status={'info'}
                  title={'Exclusions'}
                  description={buildExclusionCardDescription(resource.attr('directIndexingExcludedTickers'), resource.attr('directIndexingPersona'), () => setDirectIndexExclusionDetailsOpen(true))}
                />
              </Box>
            ) : null}
          </FDStack>
        </FDBox>
      </Box>
      <CardContainer fullWidthLayout={fullWidthLayout}>
        {/* Equity Holdings */}
        {resource.attr('equityHoldings') !== '0' && (
        <HoldingsSection
          className={classes.card}
          holdingsTypeLabel={'Stock Holdings'}
          holdingsPercentage={resource.attr('equityHoldings')}
          holdingsRelationshipLink={resource.relationship(fr.relationships.equityHoldings.name).link('related')}
        />
        )}
        {/* Alternative Income Holdings */}
        {resource.attr('alternativeIncomeHoldings') !== '0' && (
        <HoldingsSection
          className={classes.card}
          holdingsTypeLabel={'Alternative Income Holdings'}
          holdingsPercentage={resource.attr('alternativeIncomeHoldings')}
          color={'purple'}
          holdingsRelationshipLink={resource.relationship(fr.relationships.alternativeIncomeHoldings.name).link('related')}
        />
        )}
        {/* Fixed Holdings */}
        {resource.attr('fixedHoldings') !== '0' && (
        <HoldingsSection
          className={classes.card}
          holdingsTypeLabel={'Bond Holdings'}
          holdingsPercentage={resource.attr('fixedHoldings')}
          color={'secondary'}
          holdingsRelationshipLink={resource.relationship(fr.relationships.fixedHoldings.name).link('related')}
        />
        )}
        {!cashOnlyAccount && (
          <InvestmentReturnsSection
            className={classes.card}
            modelPerformanceDataRelationshipLink={resource.relationship(fr.relationships.modelPerformanceData.name).link('related')}
            modelPerformanceChartDataRelationshipLink={resource.relationship(fr.relationships.modelPerformanceChartData.name).link('related')}
          />
        )}
        {!cashOnlyAccount && (
          <CollapsibleCard
            className={classes.card}
            title={
              <Text text="Maximum Index Returns & Losses" variant="h5" weight="medium" align="start" color="black" />
                }
          >
            <MaxLossesAndReturns resource={resource} />
          </CollapsibleCard>
        )}
        <Typography className={classes.allocationInfo}>Your allocation is how your investments are balanced across stocks & bonds, if you have questions please click the link to talk to someone.</Typography>
        {pickWithPriority(resource, [
          [fr.relationships.accountOpeningTalkToSomeoneStep.name, (
            <Button1
              className={classNames(classes.linkButton, classes.noHoverStyle)}
              color={'primary'}
              variant={'underlined'}
              onClick={() => loadPageContentLink(resource.relationship(fr.relationships.accountOpeningTalkToSomeoneStep.name).link('related'))}
              test={'talk-to-someone-link-button'}
            >
              {'I\'d like to talk to someone first'}
            </Button1>
          )],
        ],
        () => null)}
        <CollapsibleDisclaimer
          disclaimerText={resource.attr('disclaimer')}
          disclaimerReadMore={resource.attr('expandedDisclaimer')}
        />
      </CardContainer>
      <FDDialog
        open={directIndexExclusionDetailsOpen}
        onClose={() => setDirectIndexExclusionDetailsOpen(false)}
      >
        <FDDialogTitle value={'Direct Indexing Exclusions'} />
        <FDDialogContent>
          <Richtext value={resource.attr('directIndexingExclusionDetails')} richtext />
        </FDDialogContent>
        <FDDialogActions>
          <FDPrimaryActionButton size={'sm'} onClick={() => setDirectIndexExclusionDetailsOpen(false)} label={'Close'} />
        </FDDialogActions>
      </FDDialog>
    </Box>
  );
};

AccountProposalReviewTemplate.defaultProps = {
  className: '',
  fullWidthLayout: true,
};

AccountProposalReviewTemplate.propTypes = {
  className: PropTypes.string,
  fullWidthLayout: PropTypes.bool,
};

export default AccountProposalReviewTemplate;
